import { SuggestionRequest, SuggestionRequestStatus } from 'types'

export interface DesignEditGeneratedFrame {
  frame_index: number
  source_url: string
  media_file_id: string | number
}

export interface DesignEditGeneratedPayload {
  frames: DesignEditGeneratedFrame[]
}

function parseGeneratedFrameItem(item: unknown): DesignEditGeneratedFrame | null {
  if (!item || typeof item !== 'object') return null
  const f = item as Record<string, unknown>
  const frameIndex = typeof f.frame_index === 'number' ? f.frame_index : Number(f.frame_index)
  const sourceUrl = typeof f.source_url === 'string' ? f.source_url : null
  const mediaFileId = f.media_file_id
  if (
    !Number.isFinite(frameIndex) ||
    !sourceUrl ||
    (typeof mediaFileId !== 'string' && typeof mediaFileId !== 'number')
  ) {
    return null
  }
  return { frame_index: frameIndex, source_url: sourceUrl, media_file_id: mediaFileId }
}

/**
 * Parses `SuggestionRequest.generated_content` after a successful design apply_edits job.
 * Shape: `{ frames: [{ frame_index, source_url, media_file_id }, ...] }`
 */
export function parseDesignEditGeneratedContent(raw: string | null): DesignEditGeneratedPayload | null {
  if (!raw) return null
  try {
    const data = JSON.parse(raw) as unknown
    if (!data || typeof data !== 'object') return null
    const frames = (data as { frames?: unknown }).frames
    if (!Array.isArray(frames) || frames.length === 0) return null

    const normalized = frames.map(parseGeneratedFrameItem).filter((f): f is DesignEditGeneratedFrame => f !== null)
    return normalized.length > 0 ? { frames: normalized } : null
  } catch {
    return null
  }
}

export interface EditDesignVariant {
  id: number
  frames: DesignEditGeneratedFrame[]
  /** `SuggestionRequest.prompt` from the edit request (operations payload shape is backend-defined). */
  sourceRequestPrompt?: unknown
}

/** Design edit apply payload often nests operations under `prompt.operations` or `prompt.design_edit_operations`. */
export function getDesignEditOperationsFromPrompt(prompt: unknown): unknown[] | null {
  if (!prompt || typeof prompt !== 'object') return null
  const p = prompt as Record<string, unknown>
  if (Array.isArray(p.operations)) return p.operations
  if (Array.isArray(p.design_edit_operations)) return p.design_edit_operations
  return null
}

/** First reference image URL from an `update_image` op for `frameIndex` when `image_urls` was sent. */
export function getFirstAttachedReferenceImageUrlForFrameFromPrompt(
  prompt: unknown,
  frameIndex: number,
): string | null {
  const ops = getDesignEditOperationsFromPrompt(prompt)
  if (!ops) return null
  for (const op of ops) {
    if (!op || typeof op !== 'object') continue
    const o = op as Record<string, unknown>
    if (o.type !== 'update_image') continue
    const fi = typeof o.frame_index === 'number' ? o.frame_index : Number(o.frame_index)
    if (!Number.isFinite(fi) || fi !== frameIndex) continue
    const urls = o.image_urls
    if (!Array.isArray(urls)) continue
    const first = urls.find((u): u is string => typeof u === 'string' && u.length > 0)
    if (first) return first
  }
  return null
}

function coercePositiveRequestId(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) return value
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value)
    if (Number.isFinite(n) && n > 0) return n
  }
  return null
}

/** Reads design-edit lineage id from `prompt.parent_request_id` (API shape) or top-level `parent_request_id`. */
export function getParentRequestIdFromSuggestionRequest(req: SuggestionRequest): number | null {
  const p =
    req.prompt && typeof req.prompt === 'object' && req.prompt !== null
      ? (req.prompt as Record<string, unknown>).parent_request_id
      : undefined
  const raw = coercePositiveRequestId(p) ?? coercePositiveRequestId(req.parent_request_id)
  if (raw == null) return null
  // Ignore self-reference (mis-stored parent); treat as root of chain.
  if (raw === req.id) return null
  return raw
}

/**
 * Walks `parent_request_id` from `startRequestId` (inclusive), newest → older, and returns the first
 * `update_image` reference URL for `frameIndex`. Does not use frame raster URLs.
 *
 * Contract: every `parent_request_id` in the chain refers to a row included in `requests` (same
 * `edit_design_requests` response as the child). Parent id is read from `prompt.parent_request_id`
 * when present, else top-level `parent_request_id`.
 */
export function getInheritedReferenceImageUrlForFrame(
  requests: SuggestionRequest[],
  startRequestId: number,
  frameIndex: number,
): string | null {
  const byId = new Map(requests.map((r) => [r.id, r]))
  const seen = new Set<number>()
  let currentId: number | null = startRequestId

  while (currentId != null && !seen.has(currentId)) {
    seen.add(currentId)
    const req = byId.get(currentId)
    // Parents are expected to be in `requests`; bail if the list is stale or incomplete.
    if (!req) break
    const fromPrompt = getFirstAttachedReferenceImageUrlForFrameFromPrompt(req.prompt, frameIndex)
    if (fromPrompt) return fromPrompt
    currentId = getParentRequestIdFromSuggestionRequest(req)
  }
  return null
}

/** Design edit requests with `id` greater than the latest committed (applied) request. */
function editRequestsSinceLastApplied(requests: SuggestionRequest[]): SuggestionRequest[] {
  const lastAppliedMaxId = requests.reduce((max, req) => {
    if (req.status !== SuggestionRequestStatus.Applied) return max
    return Math.max(max, req.id)
  }, 0)
  if (lastAppliedMaxId === 0) return requests
  return requests.filter((req) => req.id > lastAppliedMaxId)
}

const IN_FLIGHT_DESIGN_EDIT_STATUSES = new Set([
  SuggestionRequestStatus.Pending,
  SuggestionRequestStatus.Processing,
  SuggestionRequestStatus.Started,
])

/**
 * Newest in-flight design edit request id after the last applied variant, if any.
 * Used to resume polling when reopening the editor while a job is still running.
 */
export function getInFlightDesignEditRequestId(requests: SuggestionRequest[] | undefined): number | null {
  if (!requests?.length) return null
  const relevant = editRequestsSinceLastApplied(requests)
  let best: number | null = null
  for (const req of relevant) {
    if (IN_FLIGHT_DESIGN_EDIT_STATUSES.has(req.status) && (best === null || req.id > best)) {
      best = req.id
    }
  }
  return best
}

export function buildEditDesignVariantsFromRequests(requests: SuggestionRequest[] | undefined): EditDesignVariant[] {
  if (!requests?.length) return []
  const relevant = editRequestsSinceLastApplied(requests)
  const sorted = [...relevant].sort((a, b) => a.id - b.id)
  return sorted.flatMap((req) => {
    if (
      req.status === SuggestionRequestStatus.Failed ||
      req.status === SuggestionRequestStatus.Aborted ||
      req.status === SuggestionRequestStatus.Expired ||
      req.status === SuggestionRequestStatus.Applied
    ) {
      return []
    }
    const parsed = parseDesignEditGeneratedContent(req.generated_content)
    return parsed
      ? [{ id: req.id, frames: parsed.frames, sourceRequestPrompt: req.prompt }]
      : []
  })
}
